import React, { useState, useEffect } from "react";
import {
    Box,
    TextField,
    Typography,
    List,
    ListItem,
    ListItemText,
    ToggleButton,
    ToggleButtonGroup
} from "@mui/material";

const BundleDetailsStep = ({
                               products,
                               newBundle,
                               setNewBundle,
                               setValidationFunction
                           }) => {
    const [errors, setErrors] = useState({});

    useEffect(() => {
        setValidationFunction(() => validateFields);
    }, [setValidationFunction, newBundle]);

    const validateFields = () => {
        let newErrors = {};
        if (!newBundle.name.trim()) newErrors.name = "Bundle name is required.";
        if (!newBundle.sku.trim()) newErrors.sku = "Product SKU is required.";
        if (!newBundle.discount || newBundle.discount <= 0 || newBundle.discount > 100) {
            newErrors.discount = "Discount must be between 1 and 100.";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const findChild = (productId) => newBundle.productIds.find(c => c.productId === productId);

    const firstSelected = products.find(product => findChild(product.id));
    const currencySymbol = firstSelected?.defaultDisplayedPriceFormatted
        ? firstSelected.defaultDisplayedPriceFormatted.replace(/[\d.,\s]/g, '').trim()
        : '';

    const totalPrice = products
        .filter(product => findChild(product.id))
        .reduce((sum, product) => {
            const qty = findChild(product.id)?.qty || 1;
            return sum + (product.price || 0) * qty;
        }, 0);

    const totalPriceAfterDiscount = totalPrice * (1 - (newBundle.discount / 100));

    return (
        <Box sx={{ width: "100%" }}>
            <TextField
                label="Bundle Name"
                fullWidth
                value={newBundle.name}
                onChange={(e) => setNewBundle(prev => ({ ...prev, name: e.target.value }))}
                margin="normal"
                error={!!errors.name}
                helperText={errors.name}
            />
            <TextField
                label="Product SKU"
                fullWidth
                value={newBundle.sku}
                onChange={(e) => setNewBundle(prev => ({ ...prev, sku: e.target.value }))}
                margin="normal"
                error={!!errors.sku}
                helperText={errors.sku}
            />
            <TextField
                label="Discount Percentage"
                type="number"
                fullWidth
                value={newBundle.discount}
                onChange={(e) => setNewBundle(prev => ({ ...prev, discount: e.target.value }))}
                margin="normal"
                inputProps={{ min: 1, max: 100, step: 0.1 }}
                error={!!errors.discount}
                helperText={errors.discount}
            />

            <TextField
                label="Cost Price"
                type="number"
                fullWidth
                value={newBundle.costPrice}
                onChange={(e) => setNewBundle(prev => ({ ...prev, costPrice: e.target.value }))}
                margin="normal"
                inputProps={{ min: 0, step: 1 }}
            />

            <Box sx={{ mt: 2, mb: 1 }}>
                <Typography variant="subtitle2" gutterBottom>Bundle Type</Typography>
                <ToggleButtonGroup
                    value={newBundle.bundleType || 'flexible'}
                    exclusive
                    onChange={(_, val) => { if (val) setNewBundle(prev => ({ ...prev, bundleType: val })); }}
                    size="small"
                >
                    <ToggleButton value="flexible">Flexible – customer picks</ToggleButton>
                    <ToggleButton value="fixed">Fixed – set combination</ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {(newBundle.bundleType || 'flexible') === 'flexible'
                        ? 'Customer selects which product fills each slot from a dropdown.'
                        : 'Bundle has one fixed combination; included products shown in the product description.'}
                </Typography>
            </Box>

            <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1">Selected Products:</Typography>
                <List>
                    {products
                        .filter(product => findChild(product.id))
                        .map(product => (
                            <ListItem key={product.id}>
                                <ListItemText
                                    primary={`${product.name} x${findChild(product.id)?.qty || 1}`}
                                    secondary={`Price: ${product.defaultDisplayedPriceFormatted}`}
                                />
                            </ListItem>
                        ))
                    }
                </List>
                <Typography variant="h6" sx={{ mt: 2 }}>
                    Bundle Price: {currencySymbol}{totalPrice.toFixed(2)} → {currencySymbol}{totalPriceAfterDiscount.toFixed(2)}
                </Typography>
            </Box>
        </Box>


    );
};

export default BundleDetailsStep;
